/**
 * CommonJS facade for the global ready-events helpers.
 *
 * The browser globals `window.onReadyEvent` and
 * `window.dispatchReadyEvent` are defined in the ISML template:
 *   /common/scripts/readyEvents.isml
 *
 * This module forwards calls to those globals and provides JSDoc so
 * editors can offer IntelliSense.
 */

/** @typedef {() => void} UnsubscribeFn */

/**
 * @callback ReadyCallback
 * @param {Event} e
 * @returns {void}
 */

/**
 * Run a callback when `eventName` fires, or immediately if it already
 * did. Delegates to `window.onReadyEvent`.
 *
 * @param {string} eventName Event name to wait for.
 * @param {ReadyCallback} callback Handler to execute.
 * @param {EventTarget} [target=window] Event emitter (defaults to window).
 * @returns {UnsubscribeFn} Unsubscribe function.
 */
function onReadyEvent(eventName, callback, target) {
    var w = typeof window !== 'undefined' ? window : undefined;
    var t = target || w;

    if (!w || typeof w.onReadyEvent !== 'function') {
        throw new Error(
            'onReadyEvent global not found. Ensure '
        + '/common/scripts/readyEvents.isml is included.'
        );
    }

    return w.onReadyEvent(eventName, callback, t);
}

/**
 * Mark and dispatch a "ready" event. Delegates to
 * `window.dispatchReadyEvent`.
 *
 * @param {string} eventName Event name to dispatch.
 * @param {*} [detail] Optional payload for CustomEvent.detail.
 * @param {EventTarget} [target=window] Event target (defaults to window).
 * @returns {void}
 */
function dispatchReadyEvent(eventName, detail, target) {
    var w = typeof window !== 'undefined' ? window : undefined;
    var t = target || w;

    if (!w || typeof w.dispatchReadyEvent !== 'function') {
        throw new Error(
            'dispatchReadyEvent global not found. Ensure '
        + '/common/scripts/readyEvents.isml is included.'
        );
    }

    w.dispatchReadyEvent(eventName, detail, t);
}

module.exports = {
    onReadyEvent: onReadyEvent,
    dispatchReadyEvent: dispatchReadyEvent
};
